import React from 'react';
import PropTypes from 'prop-types';
import { Checkbox, TextField } from '@one-thd/sui-atomic-components';

export function UsedFilterPanel({
  categories,
  keyword,
  onKeywordChange,
  onDimensionsChange,
  onToggleCategory,
  appliedDimensions,
  onClearAllRefinements,
  stores,
  selectedStoreIds,
  onToggleStore,
}) {
  const categoryDimensionName = 'Category';
  const storeDimensionName = 'Store';

  const handleCategoryChange = (title) => {
    if (onDimensionsChange) {
      onDimensionsChange({
        dimensionName: categoryDimensionName,
        refinement: { label: title, refinementKey: `category:${title}` },
      });
      return;
    }
    onToggleCategory(title);
  };

  const handleStoreChange = (storeId) => {
    if (onDimensionsChange) {
      onDimensionsChange({
        dimensionName: storeDimensionName,
        refinement: { label: storeId, refinementKey: `store:${storeId}` },
      });
      return;
    }
    onToggleStore(storeId);
  };

  const hasAppliedRefinements = (appliedDimensions?.length ?? 0) > 0;

  return (
    <div className="sui-flex sui-flex-col sui-gap-4">
      <div>
        <TextField
          id="used-keyword-filter"
          label="Filter by Name"
          value={keyword}
          onChange={(event) => onKeywordChange(event.target.value)}
          placeholder="Type to filter..."
          fullWidth
        />
      </div>

      {hasAppliedRefinements && (
        <div className="sui-flex sui-items-center sui-justify-between">
          <p className="sui-text-sm sui-font-bold sui-m-0">Applied Filters</p>
          {onClearAllRefinements && (
            <button
              className="sui-text-sm sui-text-link"
              onClick={onClearAllRefinements}
              type="button"
            >
              Clear All
            </button>
          )}
        </div>
      )}

      {stores.length > 0 && (
        <div>
          <p className="sui-text-sm sui-font-bold sui-mb-2">Stores</p>
          <ul className="sui-list-none sui-m-0 sui-p-0 sui-flex sui-flex-col sui-gap-1">
            {stores.map((store) => (
              <li key={store.storeId} className="sui-flex sui-items-center sui-gap-2">
                <Checkbox
                  id={`store-${store.storeId}`}
                  checked={selectedStoreIds.includes(store.storeId)}
                  onChange={() => handleStoreChange(store.storeId)}
                  value={store.storeId}
                />
                <label htmlFor={`store-${store.storeId}`} className="sui-text-sm sui-cursor-pointer">
                  {store.name} #{store.storeId}
                  {store.distance != null && (
                    <span className="sui-text-subtle sui-ml-1">
                      ({Number.parseFloat(store.distance).toFixed(1)} mi)
                    </span>
                  )}
                  {store.count != null && (
                    <span className="sui-text-subtle sui-ml-1">({store.count})</span>
                  )}
                </label>
              </li>
            ))}
          </ul>
        </div>
      )}

      {categories.length > 0 && (
        <div>
          <p className="sui-text-sm sui-font-bold sui-mb-2">Category</p>
          <ul className="sui-list-none sui-m-0 sui-p-0 sui-flex sui-flex-col sui-gap-1">
            {categories.map((cat) => (
              <li key={cat.title} className="sui-flex sui-items-center sui-gap-2">
                <Checkbox
                  id={`cat-${cat.title}`}
                  checked={cat.selected}
                  onChange={() => handleCategoryChange(cat.title)}
                  value={cat.title}
                />
                <label htmlFor={`cat-${cat.title}`} className="sui-text-sm sui-cursor-pointer">
                  {cat.title}
                  <span className="sui-text-subtle sui-ml-1">({cat.count})</span>
                </label>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

UsedFilterPanel.propTypes = {
  categories: PropTypes.arrayOf(
    PropTypes.shape({
      title: PropTypes.string,
      count: PropTypes.number,
      selected: PropTypes.bool,
    })
  ).isRequired,
  keyword: PropTypes.string.isRequired,
  onKeywordChange: PropTypes.func.isRequired,
  onToggleCategory: PropTypes.func.isRequired,
  onDimensionsChange: PropTypes.func,
  appliedDimensions: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string,
      refinements: PropTypes.arrayOf(
        PropTypes.shape({
          label: PropTypes.string,
          refinementKey: PropTypes.string,
        })
      ),
    })
  ),
  onClearAllRefinements: PropTypes.func,
  stores: PropTypes.arrayOf(
    PropTypes.shape({
      storeId: PropTypes.string,
      name: PropTypes.string,
      distance: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
      count: PropTypes.number,
    })
  ).isRequired,
  selectedStoreIds: PropTypes.arrayOf(PropTypes.string).isRequired,
  onToggleStore: PropTypes.func.isRequired,
};

UsedFilterPanel.defaultProps = {
  onDimensionsChange: null,
  appliedDimensions: [],
  onClearAllRefinements: null,
};
